import { useEffect, useState } from "react";
import { fetchDashboard } from "../../../services/dashboardService";
import { DashboardStats } from "../../../types/fyo";
import PageHeader from "../../shared/PageHeader/PageHeader";
import tableauLogo    from "../../../assets/tableau_logo.svg";
import tableauPreview from "../../../assets/FYOOTT_tableau.svg";
import "./DashboardPage.css";


function TotalCard({ label, value, color }: { label: string; value: number; color: "blue" | "maroon" | "green" | "amber" | "red" }) {
  return (
    <div className={`fyo-total-card fyo-total-card--${color}`}>
      <span className="fyo-total-card__value">{value.toLocaleString()}</span>
      <span className="fyo-total-card__label">{label}</span>
    </div>
  );
}

function BreakdownCard({ title, icon, rows, onRowClick }: {
  title: string;
  icon: string;
  rows: { label: string; value: number | undefined }[];
  onRowClick?: (label: string) => void;
}) {
  return (
    <div className="fyo-breakdown-card">
      <div className="fyo-breakdown-card__head">
        <i className={`fas ${icon} fyo-breakdown-card__icon`} />
        <span className="fyo-breakdown-card__title">{title}</span>
      </div>
      <div className="fyo-breakdown-card__rows">
        {rows.map((row) =>
          onRowClick ? (
            <button
              key={row.label}
              className="fyo-breakdown__row fyo-breakdown__row--clickable"
              onClick={() => onRowClick(row.label)}
            >
              <span className="fyo-breakdown__label">{row.label}</span>
              <span className="fyo-breakdown__value">{(row.value ?? 0).toLocaleString()}</span>
            </button>
          ) : (
            <div key={row.label} className="fyo-breakdown__row">
              <span className="fyo-breakdown__label">{row.label}</span>
              <span className="fyo-breakdown__value">{(row.value ?? 0).toLocaleString()}</span>
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default function DashboardPage({ onNavigate }: { onNavigate: (path: string, state?: object) => void }) {
  const [stats,   setStats]   = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    fetchDashboard()
      .then(setStats)
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleDirectPlacementRow = (label: string) => {
    const filterMap: Record<string, object> = {
      "Actively Tracking": { status: "active"     },
      "Placed":            { status: "placed"     },
      "Tracking Expired":  { status: "not_placed" },
      "Untrackable":       { status: "paused"     },
    };
    const filters = filterMap[label];
    if (filters) onNavigate("/find-your-ohio/direct_placement", { filters });
  };

  const handleInfluencedRelocationRow = (label: string) => {
    const filterMap: Record<string, object> = {
      "Actively Tracking": { status: "active"   },
      "Moved to Ohio":      { status: "detected" },
    };
    const filters = filterMap[label];
    if (filters) onNavigate("/find-your-ohio/linkedin-tracked-ohio-relocations", { filters });
  };

  const handleInfluencedPlacementRow = (label: string) => {
    const filterMap: Record<string, object> = {
      "Actively Tracking": { status: "active"   },
      "Hired in Ohio":     { status: "detected" },
    };
    const filters = filterMap[label];
    if (filters) onNavigate("/find-your-ohio/linkedin-tracked-ohio-hires", { filters });
  };

  const handleClientPlacementRow = (label: string) => {
    const filterMap: Record<string, object> = {
      "Actively Tracking": { status: "active" },
      "Hired in Ohio":     { status: "placed" },
    };
    const filters = filterMap[label];
    if (filters) onNavigate("/find-your-ohio/linkedin-tracked-fyo-company-hires", { filters });
  };

  const c = stats?.candidates;

  return (
    <div className="fyo-page fyo-analytics-page">
      <PageHeader title="AI Analytics" subtitle="Find Your Ohio — Candidate Tracker" />

      {error && <div className="fyo-error-banner">{error}</div>}

      {loading ? (
        <p className="state-text state-text--muted">Loading...</p>
      ) : (
        <>
          {/* ── Totals ── */}
          <div className="fyo-dash__totals-grid">
            <TotalCard label="Total Candidates in Find Your Ohio Database"  value={c?.total_candidates  ?? 0} color="maroon" />
            <TotalCard label="Actively Tracking LinkedIn Status" value={c?.actively_tracking ?? 0} color="amber"  />
            <TotalCard label="Untrackable on LinkedIn"           value={c?.untrackable       ?? 0} color="red"    />
          </div>

          {/* ── Breakdown sections ── */}
          {stats && (
            <>
              <h2 className="fyo-dash__section-heading">Outcome Measure</h2>
              <div className="fyo-dash__grid">

                {/* <BreakdownCard
                  title="Direct Placement"
                  icon="fa-handshake"
                  onRowClick={handleDirectPlacementRow}
                  rows={[
                    { label: "Actively Tracking", value: stats.referrals.actively_tracking },
                    { label: "Placed",            value: stats.referrals.placed            },
                    { label: "Tracking Expired",  value: stats.referrals.tracking_expired  },
                    { label: "Untrackable",       value: stats.referrals.untrackable       },
                  ]}
                /> */}

                <BreakdownCard
                  title="LinkedIn Tracked FYO Company Hires"
                  icon="fa-briefcase"
                  onRowClick={handleClientPlacementRow}
                  rows={[
                    { label: "Actively Tracking", value: stats.client_placements?.actively_tracking },
                    { label: "Hired in Ohio",     value: stats.client_placements?.placed            },
                  ]}
                />

                <BreakdownCard
                  title="LinkedIn Tracked Ohio Relocations"
                  icon="fa-location-dot"
                  onRowClick={handleInfluencedRelocationRow}
                  rows={[
                    { label: "Actively Tracking", value: stats.ohio_moves.actively_tracking },
                    { label: "Moved to Ohio",      value: stats.ohio_moves.detected          },
                  ]}
                />

                <BreakdownCard
                  title="LinkedIn Tracked Ohio Hires"
                  icon="fa-building"
                  onRowClick={handleInfluencedPlacementRow}
                  rows={[
                    { label: "Actively Tracking", value: stats.ohio_employers.actively_tracking },
                    { label: "Hired in Ohio",     value: stats.ohio_employers.detected          },
                  ]}
                />

              </div>
            </>
          )}

          {/* ── Tracking Methodology ── */}
          <h2 className="fyo-dash__section-heading">How Candidate Tracking Works</h2>
          <div className="fyo-card fyo-dash__info-card">
            <ul className="fyo-dash__info-list">
              <li>Candidates are tracked for 3 years after entering the system.</li>
              <li>Candidate status is checked every 3 months.</li>
              <li>A valid LinkedIn profile is required for tracking.</li>
              <li>If the FYO team refers a candidate to a company, the 3 year tracking period restarts.</li>
            </ul>
          </div>

          {/* ── Tableau ── */}
          <div className="fyo-dash__tableau-heading">
            <img src={tableauLogo} alt="Tableau" width="28" height="28" />
            <h2 className="fyo-dash__section-heading" style={{ margin: 0 }}>Tableau Dashboards</h2>
          </div>
          <a
            // href="https://tableau.jobsohio.app/#/views/FindYourOhioCandidates_17810171892270/FindYourOhioCandidatesDashboard?:iid=2"
            href="https://tableau.jobsohio.app/#/views/FindYourOhioCandidates_17896053863610/FindYourOhioCandidatesDashboard?"
            target="_blank"
            rel="noopener noreferrer"
            className="fyo-dash__tableau-card"
          >
            <img
              src={tableauPreview}
              alt="Find Your Ohio Summary Dashboard preview"
              className="fyo-dash__tableau-preview"
            />
            <div className="fyo-dash__tableau-footer">
              For full detailed Find Your Ohio Analytics,{" "}
              <span className="fyo-dash__tableau-link">Click Here.</span>
            </div>
          </a>
        </>
      )}

    </div>
  );
}
